import { Transition } from '@headlessui/react';
import React, { useEffect, useMemo, useSyncExternalStore } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { getClipDurationInSeconds } from '@goldcast/player-config/src/utils/clipUtils';
import { handleClipLockedError } from '../Clip/utils';
import ClipListItemThumbnail from './ClipListItemThumbnail';
import IconButton from '@/components/atoms/Button/IconButton';
import AssetRating from '@/components/molecules/AssetRating/AssetRating';
import AIEditsTestingFlag from '@/Pages/Clip/ClipPlayer/ClipCustomizer/pocAIEdits/components/AIEditsTestingFlag';
import useSavedSearch from '@/hooks/useSavedSearch';
import { formatTime } from '@/libs/core';
import { classnames } from '@/libs/utils';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import { removeUnseenClipId, unseenClipIds } from '@/stores/unseenClips';
import Icon from '@/components/atoms/Icon';
import TooltipV2 from '@/components/TooltipV2';

export default function ClipsListItem({
  clip,
  onClipSelect,
  isCollapsed,
  children,
  animateClipList = true,
  isInPopover = false,
  isMultiSelectMode = false,
  isSelected = false,
  isSelectionLimitReached = false,
  onToggleSelection,
  hideDuration = false
}: {
  clip: Clip;
  onClipSelect: (clip: Clip) => void;
  isCollapsed: boolean;
  children?: React.ReactNode;
  animateClipList?: boolean;
  isInPopover?: boolean;
  isMultiSelectMode?: boolean;
  isSelected?: boolean;
  isSelectionLimitReached?: boolean;
  onToggleSelection?: (clipId: string) => void;
  hideDuration?: boolean;
}) {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const clipDuration = useMemo(() => {
    return formatTime(getClipDurationInSeconds(clip));
  }, [clip]);
  const [searchParams] = useSearchParams();
  const activeClipId = useMemo(() => searchParams.get('activeClipId'), [searchParams]);
  const isClipActive = activeClipId === clip.id;
  const unseenClipsStore = useSyncExternalStore(unseenClipIds.subscribe, unseenClipIds.getSnapshot);
  const isClipUnseen = unseenClipsStore.includes(clip.id);
  const { isSavedSearchPage } = useSavedSearch();

  const showAssetRating = useMemo(() => {
    return clip.asset_metadata.source === 'AI';
  }, [clip]);

  const showAIEditsTestingFlag = useMemo(() => {
    return (
      featureFlags[FeatureFlagKeys.Use_CL_Copy_Clip_To_Test_AiEdits] &&
      clip.asset_metadata.is_copied_for_ai_edits_testing
    );
  }, [featureFlags, clip.asset_metadata.is_copied_for_ai_edits_testing]);

  useEffect(() => {
    if (isClipActive) {
      const element = document.getElementById(clip.id);
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      removeUnseenClipId(clip.id);
    }
  }, [isClipActive, clip.id]);

  // !isSelected keeps the checkbox active for already-selected clips so they can still be deselected at the limit
  const isCheckboxDisabled = isSelectionLimitReached && !isSelected;

  const handleClick = () => {
    if (isMultiSelectMode) {
      if (!isCheckboxDisabled) {
        onToggleSelection?.(clip.id);
      }
    } else {
      onClipSelect(clip);
    }
  };

  return (
    <div
      data-testid="clip-list-item"
      id={clip.id}
      role="button"
      onClick={handleClick}
      className={classnames('group flex w-full cursor-pointer items-stretch space-x-4 px-5 py-3 pr-3 text-left', {
        '!bg-slate-100': isClipActive && !isMultiSelectMode,
        'bg-orange-50': isClipUnseen && !isMultiSelectMode,
        'hover:bg-slate-50': !children && !isMultiSelectMode,
        'hover:bg-slate-100': isMultiSelectMode
      })}
    >
      <div
        className={classnames(
          'relative flex aspect-[4/3] shrink-0 flex-col items-center justify-center rounded-xl bg-slate-200',
          {
            '!bg-orange-100': isClipUnseen && !isClipActive
          },
          isInPopover ? 'w-[184px]' : 'w-56'
        )}
        id={clip.asset_metadata.id}
        title={`${clip.title}\n\n${clip.asset_metadata.description || ''}`}
      >
        <ClipListItemThumbnail clip={clip} isClipActive={isClipActive} isClipUnseen={isClipUnseen} />
      </div>

      <Transition
        show={!isCollapsed}
        appear={animateClipList}
        className="min-w-0 grow"
        enter="transition-all delay-300 duration-300 overflow-hidden"
        enterFrom="opacity-0 translate-x-[-2rem]"
        enterTo="opacity-100"
        leave="transition-all duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="flex h-full grow flex-col justify-between pt-0.5">
          <div className="flex w-full items-center justify-between space-x-1">
            <div className="flex grow justify-between space-x-1">
              <div data-testid="clip-description" title={clip.asset_metadata.description} className="min-w-0 flex-1">
                <div
                  data-testid="clip-title"
                  className={classnames(
                    'grow text-sm font-medium leading-tight',
                    isInPopover ? 'line-clamp-2' : 'line-clamp-3'
                  )}
                >
                  {clip.title}
                </div>
                {isSavedSearchPage ? (
                  <div
                    data-testid="clip-recording-link"
                    className="mt-1.5 flex shrink-0 items-center space-x-0.5 text-xs text-slate-500 hover:underline"
                  >
                    <Icon name="IconBrandYoutube" size={16} className="shrink-0" />
                    <Link
                      to={`/${clip.content.project.id}/${clip.content.id}/recordings`}
                      title={clip.content.title}
                      target="_blank"
                      className="max-w-36"
                    >
                      <span className="line-clamp-1">{clip.content.title}</span>
                    </Link>
                  </div>
                ) : clip.asset_metadata.description ? (
                  <div
                    className={classnames(
                      'mt-1 shrink-0 text-sm text-slate-600',
                      isInPopover ? 'line-clamp-2' : 'line-clamp-3'
                    )}
                  >
                    {clip.asset_metadata.description}
                  </div>
                ) : null}
                {showAIEditsTestingFlag && (
                  <div className="mt-1">
                    <AIEditsTestingFlag />
                  </div>
                )}
              </div>
              {isMultiSelectMode ? (
                <div
                  className="ml-2 flex shrink-0 items-start pt-0.5"
                  onClick={e => {
                    e.stopPropagation();
                    if (!isCheckboxDisabled) {
                      onToggleSelection?.(clip.id);
                    }
                  }}
                >
                  {isCheckboxDisabled ? (
                    <TooltipV2
                      tooltipId={`clip-select-limit-${clip.id}`}
                      tooltipContent="You can select up to 10 clips at a time"
                      tooltipElement={
                        <div
                          data-testid="clip-checkbox"
                          className="flex size-5 cursor-not-allowed items-center justify-center rounded border border-slate-200 bg-slate-100"
                        />
                      }
                    />
                  ) : (
                    <div
                      data-testid="clip-checkbox"
                      className={classnames(
                        'flex size-5 cursor-pointer items-center justify-center rounded',
                        isSelected ? 'bg-slate-800' : 'border border-slate-300 bg-white'
                      )}
                    >
                      {isSelected && <Icon name="IconCheck" size={16} stroke={2.5} color="white" />}
                    </div>
                  )}
                </div>
              ) : (
                children
              )}
            </div>
          </div>
          <div className={classnames('flex w-full items-center', hideDuration ? 'justify-end' : 'justify-between')}>
            {!hideDuration && (
              <div data-testid="clip-duration" className="text-xs text-slate-600">
                {clipDuration}
              </div>
            )}
            <div className="flex text-slate-600">
              {showAssetRating && <AssetRating assetId={clip?.id} rating={clip?.rating} assetType="CLIP" />}
              {clip.locked ? (
                <IconButton
                  icon="IconLock"
                  title={`Clip is published in ${clip.content.project.title}'s on-demand page and cannot be edited. Please un-publish to continue editing.`}
                  onClick={() => handleClipLockedError(clip)}
                  trackingId="clips-list-item-locked-button"
                  variation="outline"
                  iconClassName="h-4 w-4 text-slate-600"
                  buttonClassName="h-6 w-4"
                />
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
}
